import { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/layout/layout";
import { useSeo } from "@/lib/seo";
import {
  useListVenueMembershipPlans,
  useCreateVenueApplication,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle2,
  Loader2,
  MapPin,
  ShieldCheck,
  Sparkles,
  Trophy,
  Users,
  Timer,
  Coins,
  RotateCcw,
  ClipboardList,
  Megaphone,
  QrCode,
  Mail,
  BarChart3,
  CreditCard,
  Smartphone,
  Calendar,
  History,
  ListOrdered,
  FileStack,
  UserCheck,
  KeyRound,
  Trash2,
  ArrowRight,
  Wallet,
  TrendingUp,
  Zap,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import logoPath from "@/assets/poker-logo.png";

type Interval = "monthly" | "yearly";

function AdminPreviewFrame({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl overflow-hidden border border-slate-700/70 bg-slate-900 shadow-2xl shadow-primary/10 ring-1 ring-primary/10">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-950/80 border-b border-slate-700/60">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
        </div>
        <div className="ml-3 flex-1 min-w-0">
          <div className="text-xs text-slate-300 font-semibold leading-tight truncate">{title}</div>
          {subtitle && (
            <div className="text-[10px] text-slate-500 font-mono leading-tight truncate">{subtitle}</div>
          )}
        </div>
        <div className="hidden sm:block text-[10px] uppercase tracking-wider text-amber-400/80 font-bold">
          Admin
        </div>
      </div>
      {children}
    </div>
  );
}

function fmtMoney(cents: number, currency: string) {
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

const HERO_PROPS = [
  {
    icon: Wallet,
    title: "0% commission, ever",
    body: "Every dollar of buy-ins, re-buys and bounties stays with your venue. We don't touch the prize pool, the rake, or the bar.",
  },
  {
    icon: TrendingUp,
    title: "Fill your slow nights",
    body: "Your tournaments hit our public schedule, leaderboards, and player emails the moment you publish them.",
  },
  {
    icon: Zap,
    title: "Ready in 10 minutes",
    body: "Apply online, pay your membership, get an instant admin login. Run your first tournament tonight.",
  },
];

type FeatureGroup = {
  title: string;
  blurb: string;
  features: { icon: React.ComponentType<{ className?: string }>; title: string; body: string }[];
};

const FEATURE_GROUPS: FeatureGroup[] = [
  {
    title: "Run the tournament",
    blurb: "Everything you need from sign-ups to payouts, in one screen.",
    features: [
      {
        icon: Trophy,
        title: "Tournaments & templates",
        body: "Create tournaments in seconds — or save your weekly setup as a template and clone it with one click.",
      },
      {
        icon: Timer,
        title: "Fullscreen blind timer",
        body: "TV-ready blind clock with audio cues. Run it from your phone as a remote — no laptop on the table.",
      },
      {
        icon: Coins,
        title: "Custom blind & payout structures",
        body: "Build reusable blind schedules and payout tiers. Assign them to any tournament in two clicks.",
      },
      {
        icon: RotateCcw,
        title: "Re-buys & bounties",
        body: "Per-player re-buy counters, bounty tracking, and prize-pool maths handled automatically.",
      },
    ],
  },
  {
    title: "Sign-ups & seating",
    blurb: "Players RSVP from their phone. You spend the night dealing, not chasing names.",
    features: [
      {
        icon: Users,
        title: "Online RSVPs & waitlist",
        body: "Capacity-aware registration with auto-promotion from waitlist when seats free up.",
      },
      {
        icon: QrCode,
        title: "QR scan-in",
        body: "Print player badges and scan them in with the camera at the door — no app required.",
      },
      {
        icon: Calendar,
        title: "Late-rego window",
        body: "Set a late-registration cutoff per tournament; the platform enforces it automatically.",
      },
      {
        icon: Smartphone,
        title: "Player phone dashboard",
        body: "Players see their upcoming games, history, and points without downloading anything.",
      },
    ],
  },
  {
    title: "Money in, money out",
    blurb: "Take card payments online — or stick with cash. Your venue, your call.",
    features: [
      {
        icon: CreditCard,
        title: "Card buy-ins via Stripe",
        body: "Players can pre-pay with card. Funds settle straight to your venue's connected Stripe account.",
      },
      {
        icon: Wallet,
        title: "Cash & event-ticket flow",
        body: "Mark cash, comp tickets, or member event-tickets at the door. Full audit trail on every dollar.",
      },
      {
        icon: BarChart3,
        title: "Per-venue P&L",
        body: "Revenue, payouts, ROI, average field, and average buy-in — by night, by week, by venue.",
      },
      {
        icon: Mail,
        title: "Weekly financial emails",
        body: "Automatic CSV financial summary emailed on a schedule you choose. Zero spreadsheet juggling.",
      },
    ],
  },
  {
    title: "Players & community",
    blurb: "Build a roster that comes back week after week.",
    features: [
      {
        icon: ListOrdered,
        title: "League leaderboards & seasons",
        body: "Run multiple seasons per league, with reproducible historical points formulas.",
      },
      {
        icon: ClipboardList,
        title: "Results in seconds",
        body: "Bulk-enter finishing positions; the system does the points, prize-pool splits, and audit log.",
      },
      {
        icon: Megaphone,
        title: "Site-wide announcements",
        body: "Push a banner across the player site for venue-changes, jackpot games, or weather call-offs.",
      },
      {
        icon: Mail,
        title: "Lapsed-regular nudges",
        body: "We surface players who've gone quiet — and let you send a one-click 'we miss you' email.",
      },
    ],
  },
  {
    title: "Staff & control",
    blurb: "Add your tournament directors. Lock down what they can see.",
    features: [
      {
        icon: UserCheck,
        title: "Staff invites with roles",
        body: "Invite TDs and floor staff by email. Built-in roles + your own custom roles.",
      },
      {
        icon: ShieldCheck,
        title: "Per-venue staff scoping",
        body: "Multi-venue groups: restrict staff to only the venues they work at. No accidental edits.",
      },
      {
        icon: KeyRound,
        title: "Self-service password reset",
        body: "Forgot-password and single-use reset links built in. No more support tickets to you.",
      },
      {
        icon: FileStack,
        title: "Tournament templates",
        body: "Save your standard $50 Tuesday or $100 Friday once — relaunch it every week.",
      },
    ],
  },
  {
    title: "Trust & safety",
    blurb: "Built so you can confidently hand the keys to anyone on staff.",
    features: [
      {
        icon: History,
        title: "Full audit log",
        body: "Every change to tournaments, results, registrations, re-buys, and roles — with before/after diff.",
      },
      {
        icon: Trash2,
        title: "30-day trash & restore",
        body: "Soft-delete on everything. Accidentally cancelled a tournament? Restore in one click.",
      },
      {
        icon: ShieldCheck,
        title: "Suspicious-activity feed",
        body: "Failed sign-ins, throttled invite tokens, and player-auth abuse — all surfaced in one place.",
      },
      {
        icon: Sparkles,
        title: "Daily backups",
        body: "Daily platform snapshots plus weekly off-site database backups. Your data is safe.",
      },
    ],
  },
];

const FAQS = [
  {
    q: "Do you take a cut of buy-ins or prize pools?",
    a: "No. Zero. Your membership covers everything — the platform, the timer, the player site, the dashboard, the emails, the support. Buy-ins, re-buys, bounties, and prize money never touch our books.",
  },
  {
    q: "Can my players pay with cash?",
    a: "Yes — cash is fully supported alongside card payments and event tickets. Use whichever mix suits your venue.",
  },
  {
    q: "What about card payments?",
    a: "Optional. If you turn on Stripe payouts, players can pre-pay buy-ins by card and the funds land directly in your venue's bank account. We don't sit in the middle.",
  },
  {
    q: "How long does approval take?",
    a: "Usually within one to two business days. We just confirm the venue is real and licensed to host the games. Once approved, your venue goes live on the player site.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Membership is monthly or yearly with no lock-in contracts. Cancel from the Stripe portal or just email us.",
  },
  {
    q: "Do you charge per tournament or per player?",
    a: "No per-tournament fees. No per-player fees. No transaction fees on top of Stripe's standard rate. Just your flat membership.",
  },
];

export default function HostYourVenue() {
  useSeo({
    title: "Host Poker at Your Venue",
    description:
      "Run pub poker tournaments at your venue with Interclub Poker Circuit. Free leaderboards, scheduling tools, and a player network already looking for games.",
    canonicalPath: "/host-your-venue",
  });
  const { toast } = useToast();
  const params = new URLSearchParams(window.location.search);
  const successFlag = params.get("host") === "success";
  const cancelledFlag = params.get("host") === "cancelled";

  const { data: plans, isLoading: plansLoading } = useListVenueMembershipPlans();
  const apply = useCreateVenueApplication();
  const [interval, setInterval] = useState<Interval>("yearly");
  const [form, setForm] = useState({
    name: "",
    contactEmail: "",
    contactPhone: "",
    websiteUrl: "",
    address: "",
    city: "",
    state: "",
    capacity: "",
    notes: "",
  });

  useEffect(() => {
    if (cancelledFlag) {
      toast({
        title: "Checkout cancelled",
        description: "Your application is saved. You can re-try payment from the email we sent.",
      });
    }
  }, [cancelledFlag, toast]);

  const monthly = useMemo(() => plans?.find((p) => p.interval === "monthly") ?? null, [plans]);
  const yearly = useMemo(() => plans?.find((p) => p.interval === "yearly") ?? null, [plans]);

  if (successFlag) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 max-w-2xl text-center">
          <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold tracking-tight">Thanks — application received!</h1>
          <p className="mt-4 text-muted-foreground">
            Your subscription is active and your venue is in our review queue. We'll
            email you within 1–2 business days once a Circuit organiser approves your
            listing. You'll then see your venue on the public Venues page.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Need to add bank details for tournament payouts? You'll get a separate
            secure link from Stripe once approved.
          </p>
          <a href="/" className="inline-block mt-6">
            <Button variant="outline">Back to home</Button>
          </a>
        </div>
      </Layout>
    );
  }

  const onChange = <K extends keyof typeof form>(k: K, v: string) =>
    setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contactEmail.trim()) {
      toast({ variant: "destructive", title: "Please fill in name and contact email." });
      return;
    }
    try {
      const capacity = form.capacity.trim() ? Number(form.capacity) : null;
      const res = await apply.mutateAsync({
        data: {
          name: form.name.trim(),
          contactEmail: form.contactEmail.trim(),
          contactPhone: form.contactPhone.trim() || null,
          websiteUrl: form.websiteUrl.trim() || null,
          address: form.address.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          capacity: Number.isFinite(capacity as number) ? capacity : null,
          notes: form.notes.trim() || null,
          interval,
        },
      });
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
      } else {
        toast({
          title: "Application submitted",
          description: "We'll be in touch shortly to confirm payment and approval.",
        });
      }
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      const detail = (err as { data?: { error?: string } } | null)?.data?.error;
      toast({
        variant: "destructive",
        title: "Couldn't submit application",
        description: detail ?? (status === 400
          ? "Please check the form and try again."
          : "Something went wrong. Please try again."),
      });
    }
  };

  const PlanCard = ({ p, badge }: { p: NonNullable<typeof monthly>; badge?: string }) => {
    const selected = interval === p.interval;
    return (
      <button
        type="button"
        onClick={() => setInterval(p.interval as Interval)}
        data-testid={`plan-${p.interval}`}
        className={`relative w-full text-left rounded-2xl border-2 p-6 transition-all ${
          selected
            ? "border-primary bg-primary/5 shadow-md"
            : "border-border/60 bg-card hover:border-primary/40"
        }`}
      >
        {badge && (
          <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-0.5 text-[11px] font-bold uppercase tracking-wider text-primary-foreground">
            {badge}
          </span>
        )}
        <div className="flex items-center justify-between">
          <span className="text-sm uppercase tracking-wider font-semibold text-muted-foreground">
            {p.interval}
          </span>
          {selected && <CheckCircle2 className="h-5 w-5 text-primary" />}
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-4xl font-bold">{fmtMoney(p.priceCents, p.currency)}</span>
          <span className="text-sm text-muted-foreground">
            / {p.interval === "monthly" ? "month" : "year"}
          </span>
        </div>
        {p.interval === "yearly" && monthly && (
          <p className="mt-2 text-xs font-medium text-primary">
            Save {fmtMoney(monthly.priceCents * 12 - p.priceCents, p.currency)} vs monthly
          </p>
        )}
        {p.trialDays > 0 && (
          <p className="mt-2 text-xs font-semibold text-primary">
            {p.trialDays}-day free trial — you won't be charged until day {p.trialDays + 1}.
          </p>
        )}
        <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
          {p.trialDays > 0 && (
            <li className="flex gap-2">
              <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {p.trialDays}-day free trial
            </li>
          )}
          <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /> Unlimited tournaments & players</li>
          <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /> Unlimited staff accounts</li>
          <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /> 0% commission on prize pools</li>
          <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /> Full feature access from day one</li>
          <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /> Cancel anytime</li>
        </ul>
      </button>
    );
  };

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-card to-background border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-center">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary mb-6">
                <Sparkles className="h-3.5 w-3.5" /> For pubs, clubs &amp; poker hosts
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                Run a poker night.{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-500">
                  Keep every dollar.
                </span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                Interclub Poker Circuit gives your venue the same admin dashboard,
                blind timer, player app, and leaderboards the big rooms use — for one
                flat membership. <span className="text-foreground font-semibold">No commissions. No transaction fees. No per-player charges.</span>
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href="#apply">
                  <Button size="lg" className="w-full sm:w-auto h-14 px-8 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90">
                    List your venue <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <a href="#features">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto h-14 px-8 text-base font-semibold">
                    See every feature
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative flex items-center justify-center order-first lg:order-last">
              <div className="absolute inset-0 -z-10 bg-primary/20 blur-3xl rounded-full" aria-hidden />
              <img
                src={logoPath}
                alt="Interclub Poker Circuit"
                className="w-44 sm:w-56 lg:w-72 h-auto object-contain drop-shadow-[0_18px_45px_rgba(212,175,55,0.45)]"
              />
            </div>
          </div>

          {/* Top-line value props */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {HERO_PROPS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="rounded-2xl border border-border/60 bg-card/60 p-6 backdrop-blur">
                  <div className="h-11 w-11 rounded-lg bg-primary/15 text-primary flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMMISSION BANNER */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 max-w-6xl">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div>
              <p className="text-sm uppercase tracking-wider font-bold text-secondary-foreground/70">
                Honest pricing
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold mt-1">
                The prize pool stays yours. Always.
              </h2>
              <p className="mt-2 text-secondary-foreground/80 max-w-2xl">
                Other platforms take 5–10% off the top of every buy-in. We don't.
                Pay your membership, then run as many tournaments as you like with
                as many players as you like — at the same flat price.
              </p>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl sm:text-6xl font-extrabold text-primary">0%</span>
              <span className="text-secondary-foreground/70 font-medium">commission</span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section id="features" className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-wider font-bold text-primary">
              Everything in the box
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">
              The full admin dashboard, included.
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Every feature below is unlocked from day one — no add-ons, no premium tier,
              no upsells. If we build it, you get it.
            </p>
          </div>

          <div className="mt-12 space-y-16">
            {FEATURE_GROUPS.map((group) => (
              <div key={group.title}>
                <div className="border-l-4 border-primary pl-4 mb-8">
                  <h3 className="text-2xl font-bold tracking-tight">{group.title}</h3>
                  <p className="text-muted-foreground mt-1">{group.blurb}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {group.features.map((f) => {
                    const Icon = f.icon;
                    return (
                      <div
                        key={f.title}
                        className="rounded-xl border border-border/60 bg-card p-5 hover:border-primary/40 hover:shadow-sm transition-all"
                      >
                        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                          <Icon className="h-5 w-5" />
                        </div>
                        <h4 className="font-bold text-foreground">{f.title}</h4>
                        <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ADMIN PREVIEW */}
      <section className="py-20 bg-background border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-wider font-bold text-primary">
              Inside the admin
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">
              A look at the dashboard you'll get.
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Built dark-mode-first, optimised for the floor staff running your night.
              Here's a peek at the screens they'll live in.
            </p>
          </div>

          <div className="mt-12 space-y-12">
            {/* Dashboard mockup */}
            <AdminPreviewFrame title="Dashboard" subtitle="/admin/dashboard">
              <div className="p-6 space-y-6">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400">Welcome back</div>
                  <div className="text-2xl font-bold text-white mt-1">Here's tonight at a glance</div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: "Active players", value: "1,284", trend: "+38" },
                    { label: "Tournaments run", value: "412", trend: "+6" },
                    { label: "Total prize money", value: "$182,450", trend: "+$4,120" },
                    { label: "Tonight's seats", value: "84 / 96", trend: "12 waitlist" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-lg bg-slate-800/60 border border-slate-700/60 p-4">
                      <div className="text-[11px] uppercase tracking-wider text-slate-400">{s.label}</div>
                      <div className="text-2xl font-bold text-white mt-1.5">{s.value}</div>
                      <div className="text-xs text-amber-400 mt-1">{s.trend}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-lg bg-slate-800/40 border border-slate-700/60 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-bold text-white">Recent tournaments</div>
                      <div className="text-xs text-amber-400">View all →</div>
                    </div>
                    <ul className="divide-y divide-slate-700/50">
                      {[
                        { name: "Tuesday $50 Freezeout", date: "Tue 7pm", entries: 42, status: "Active" },
                        { name: "Friday Bounty Hunter", date: "Fri 7:30pm", entries: 64, status: "Upcoming" },
                        { name: "Sunday Deepstack", date: "Sun 4pm", entries: 38, status: "Completed" },
                      ].map((t) => (
                        <li key={t.name} className="py-2.5 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-100 truncate">{t.name}</div>
                            <div className="text-xs text-slate-400">{t.date} · {t.entries} entries</div>
                          </div>
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold shrink-0 ${
                            t.status === "Active" ? "bg-emerald-500/20 text-emerald-300" :
                            t.status === "Upcoming" ? "bg-amber-500/20 text-amber-300" :
                            "bg-slate-500/20 text-slate-300"
                          }`}>{t.status}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-lg bg-slate-800/40 border border-slate-700/60 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-bold text-white">Top players this season</div>
                      <div className="text-xs text-amber-400">Leaderboard →</div>
                    </div>
                    <ul className="space-y-2">
                      {[
                        { rank: 1, name: "Sarah K.", pts: 1840 },
                        { rank: 2, name: "Mick D.", pts: 1722 },
                        { rank: 3, name: "Joel B.", pts: 1650 },
                        { rank: 4, name: "Priya N.", pts: 1495 },
                      ].map((p) => (
                        <li key={p.rank} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                              p.rank === 1 ? "bg-yellow-500/20 text-yellow-300" :
                              p.rank === 2 ? "bg-slate-300/20 text-slate-200" :
                              p.rank === 3 ? "bg-amber-700/30 text-amber-400" :
                              "bg-slate-700 text-slate-300"
                            }`}>{p.rank}</div>
                            <div className="text-sm text-slate-100 font-medium truncate">{p.name}</div>
                          </div>
                          <div className="text-sm font-bold text-amber-400 shrink-0">{p.pts.toLocaleString()} pts</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </AdminPreviewFrame>

            {/* Tournaments + Timer side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <AdminPreviewFrame title="Tournament Management" subtitle="/admin/tournaments">
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4 gap-2">
                    <div className="flex items-center gap-2">
                      <div className="h-7 px-2.5 rounded-md bg-slate-700/60 text-[11px] text-slate-300 inline-flex items-center">League: All</div>
                      <div className="h-7 px-2.5 rounded-md bg-slate-700/60 text-[11px] text-slate-300 inline-flex items-center hidden sm:inline-flex">Status: Upcoming</div>
                    </div>
                    <div className="h-7 px-3 rounded-md bg-amber-500 text-slate-900 text-[11px] font-bold inline-flex items-center">+ New Tournament</div>
                  </div>
                  <div className="rounded-md border border-slate-700/60 overflow-hidden">
                    <div className="grid grid-cols-[1.6fr_0.7fr_0.7fr_0.7fr] bg-slate-800/80 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                      <div>Tournament</div><div>Date</div><div>Buy-in</div><div className="text-right">Entries</div>
                    </div>
                    {[
                      { n: "Tuesday $50 Freezeout", d: "Tue 7:00pm", b: "$50", e: "42 / 60" },
                      { n: "Friday Bounty Hunter", d: "Fri 7:30pm", b: "$80 + $20", e: "64 / 80" },
                      { n: "Sunday Deepstack", d: "Sun 4:00pm", b: "$120", e: "38 / 50" },
                      { n: "Monthly Main Event", d: "Sat May 24", b: "$330", e: "12 / 120" },
                      { n: "Wednesday Freeroll", d: "Wed 7:00pm", b: "Free", e: "84 / 100" },
                    ].map((r, i) => (
                      <div key={i} className="grid grid-cols-[1.6fr_0.7fr_0.7fr_0.7fr] px-3 py-2.5 text-xs border-t border-slate-700/40 items-center">
                        <div className="text-slate-100 font-medium truncate">{r.n}</div>
                        <div className="text-slate-400">{r.d}</div>
                        <div className="text-amber-400 font-semibold">{r.b}</div>
                        <div className="text-right text-slate-300">{r.e}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </AdminPreviewFrame>

              <AdminPreviewFrame title="Blind Timer" subtitle="/admin/timer/:id">
                <div className="p-6 flex flex-col items-center text-center">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">Level 6 of 18</div>
                  <div className="text-6xl sm:text-7xl font-mono font-extrabold text-white tabular-nums mt-3 tracking-tighter">
                    14:32
                  </div>
                  <div className="mt-4 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-slate-800/60 border border-slate-700/60">
                    <span className="text-xs text-slate-400">Blinds</span>
                    <span className="text-lg font-bold text-amber-400 tabular-nums">200 / 400</span>
                    <span className="text-xs text-slate-500">·</span>
                    <span className="text-xs text-slate-400">Ante</span>
                    <span className="text-sm font-semibold text-slate-200 tabular-nums">50</span>
                  </div>
                  <div className="mt-5 grid grid-cols-3 gap-3 w-full text-center">
                    <div className="rounded-md bg-slate-800/60 border border-slate-700/50 py-2">
                      <div className="text-[10px] uppercase text-slate-400">Players</div>
                      <div className="text-lg font-bold text-slate-100 mt-0.5">38 / 64</div>
                    </div>
                    <div className="rounded-md bg-slate-800/60 border border-slate-700/50 py-2">
                      <div className="text-[10px] uppercase text-slate-400">Avg stack</div>
                      <div className="text-lg font-bold text-slate-100 mt-0.5">21,450</div>
                    </div>
                    <div className="rounded-md bg-slate-800/60 border border-slate-700/50 py-2">
                      <div className="text-[10px] uppercase text-slate-400">Prize pool</div>
                      <div className="text-lg font-bold text-amber-400 mt-0.5">$5,120</div>
                    </div>
                  </div>
                  <div className="mt-4 text-[11px] text-slate-400">
                    Next break in <span className="text-slate-200 font-semibold">29:32</span> · Phone-as-remote ready
                  </div>
                </div>
              </AdminPreviewFrame>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 bg-card border-y border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm uppercase tracking-wider font-bold text-primary">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">
              One flat fee. That's the whole bill.
            </h2>
            <p className="mt-4 text-muted-foreground">
              Pick monthly or yearly below — same features either way. Yearly saves you a
              couple of months and locks in the price.
            </p>
          </div>

          <div className="mt-10">
            {plansLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !monthly && !yearly ? (
              <p className="text-center text-muted-foreground">No plans available right now. Check back soon.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {monthly && <PlanCard p={monthly} />}
                {yearly && <PlanCard p={yearly} badge="Best value" />}
              </div>
            )}
          </div>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            Card payments on buy-ins use Stripe's standard processing rate (paid by the
            player, not by you). We never take a cut.
          </p>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-sm uppercase tracking-wider font-bold text-primary">Get started</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">
              Tell us about your venue
            </h2>
            <p className="mt-4 text-muted-foreground">
              Two minutes to fill in. You'll go straight to a secure Stripe checkout for your
              membership, then we'll email you once we've approved the listing.
            </p>
          </div>

          <Card className="bg-card border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" /> Venue details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="hv-name">Venue name *</Label>
                  <Input
                    id="hv-name"
                    data-testid="input-venue-name"
                    value={form.name}
                    onChange={(e) => onChange("name", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="hv-email">Contact email *</Label>
                  <Input
                    id="hv-email"
                    type="email"
                    data-testid="input-contact-email"
                    value={form.contactEmail}
                    onChange={(e) => onChange("contactEmail", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="hv-phone">Contact phone</Label>
                  <Input
                    id="hv-phone"
                    data-testid="input-contact-phone"
                    value={form.contactPhone}
                    onChange={(e) => onChange("contactPhone", e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="hv-web">Website</Label>
                  <Input
                    id="hv-web"
                    type="url"
                    placeholder="https://"
                    data-testid="input-website"
                    value={form.websiteUrl}
                    onChange={(e) => onChange("websiteUrl", e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="hv-addr">Street address</Label>
                  <Input
                    id="hv-addr"
                    data-testid="input-address"
                    value={form.address}
                    onChange={(e) => onChange("address", e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="hv-city">City / Suburb</Label>
                  <Input
                    id="hv-city"
                    data-testid="input-city"
                    value={form.city}
                    onChange={(e) => onChange("city", e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="hv-state">State</Label>
                  <Input
                    id="hv-state"
                    data-testid="input-state"
                    value={form.state}
                    onChange={(e) => onChange("state", e.target.value)}
                    placeholder="NSW, VIC, QLD, WA, SA, TAS, NT"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="hv-cap">Seating capacity</Label>
                  <Input
                    id="hv-cap"
                    type="number"
                    min={1}
                    data-testid="input-capacity"
                    value={form.capacity}
                    onChange={(e) => onChange("capacity", e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="hv-notes">Anything else? (optional)</Label>
                  <Textarea
                    id="hv-notes"
                    data-testid="input-notes"
                    rows={3}
                    value={form.notes}
                    onChange={(e) => onChange("notes", e.target.value)}
                    placeholder="Best nights, food/drink offering, parking, etc."
                  />
                </div>

                <div className="sm:col-span-2 pt-2 border-t border-border/50">
                  <p className="text-sm font-medium mb-3">
                    Selected plan: <span className="text-primary font-bold uppercase">{interval}</span>
                    {interval === "monthly" && monthly && (
                      <> — {fmtMoney(monthly.priceCents, monthly.currency)} / month</>
                    )}
                    {interval === "yearly" && yearly && (
                      <> — {fmtMoney(yearly.priceCents, yearly.currency)} / year</>
                    )}
                    {" · "}
                    <a href="#pricing" className="text-primary underline-offset-2 hover:underline">change</a>
                  </p>
                </div>

                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
                  <Button
                    type="submit"
                    size="lg"
                    data-testid="submit-application"
                    disabled={apply.isPending || (!monthly && !yearly)}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {apply.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Continue to secure checkout
                  </Button>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Cards processed by Stripe. Cancel anytime.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-card border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-sm uppercase tracking-wider font-bold text-primary">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">
              Straight answers
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FAQS.map((f) => (
              <div key={f.q} className="rounded-xl border border-border/60 bg-background p-6">
                <h3 className="font-bold text-foreground">{f.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a href="#apply">
              <Button size="lg" className="h-14 px-10 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90">
                List your venue today <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
